/**
 * Where a fixed-position dropdown panel goes.
 *
 * Three controls open a `position: fixed` panel anchored to a trigger — both
 * date pickers and `app-select` — and each has to solve the same two problems:
 * not falling off the bottom of the viewport, and not falling off the side.
 * `app-select` carried its own copy until it became the third, which is what
 * made the duplication worth removing.
 *
 * The two pickers pass a literal `fallback` because their panels have intrinsic
 * widths; `app-select`'s tracks its trigger, so it derives one. That is the only
 * reason `fallback` is a parameter rather than a constant here.
 *
 * ONE TRAP, and it is the reason this is worth stating once rather than three
 * times: no ancestor of the panel may carry a transform. Any non-`none`
 * `transform`/`translate` makes that element the containing block for `fixed`
 * descendants, so the panel stops resolving against the viewport and is clipped
 * by that ancestor's box instead. A mobile drawer sliding on `-translate-x-full`
 * is the usual culprit — slide it with `left` instead. The Task History filter
 * rail carries a comment saying exactly that, because it hit this.
 */

export interface PanelPlacement {
  top: number;
  left: number;
  /** True when the panel was flipped above the trigger for lack of room below. */
  flipped: boolean;
}

/** Gap between the trigger and the panel, and the minimum viewport inset. */
const GAP = 4;

/**
 * Pin a panel to its trigger.
 *
 * Call on open and again on every scroll and resize while open — a `fixed`
 * position captured once drifts away from its trigger the moment anything
 * scrolls.
 *
 * `panel` is null on the first pass, which runs before the panel is rendered so
 * it never paints at 0,0; `fallback` is what the flip decision uses until there
 * is a real box to measure. Call again from a `requestAnimationFrame` once the
 * panel exists.
 */
export function placePanel(
  trigger: HTMLElement,
  panel: HTMLElement | null | undefined,
  fallback: { height: number; width: number },
): PanelPlacement {
  const rect = trigger.getBoundingClientRect();
  const height = panel?.offsetHeight || fallback.height;
  const width = panel?.offsetWidth || fallback.width;

  const spaceBelow = window.innerHeight - rect.bottom;
  const flipped = spaceBelow < height + GAP && rect.top > spaceBelow;

  // Clamped so a trigger near either edge does not push the panel off-screen.
  // The panel is wider than the field it hangs off in both pickers, so without
  // this a trigger in a left-hand rail has its calendar cut off by the viewport.
  const left = Math.max(
    GAP,
    Math.min(rect.left, window.innerWidth - width - GAP),
  );

  return {
    top: flipped ? Math.max(GAP, rect.top - height - GAP) : rect.bottom + GAP,
    left,
    flipped,
  };
}
